const STORAGE_KEYS = {
  BUSINESS_ID: 'pulsify_business_id',
  LISTING_ID: 'pulsify_listing_id',
  ADMIN_VIEWING_BUSINESS_ID: 'pulsify_admin_viewing_business_id',
  ADMIN_VIEWING_BUSINESS_DATA: 'pulsify_admin_viewing_business_data',
  IS_ADMIN: 'pulsify_is_admin',
} as const

const SESSION_KEYS = [
  STORAGE_KEYS.BUSINESS_ID,
  STORAGE_KEYS.LISTING_ID,
  STORAGE_KEYS.ADMIN_VIEWING_BUSINESS_ID,
  STORAGE_KEYS.ADMIN_VIEWING_BUSINESS_DATA,
  STORAGE_KEYS.IS_ADMIN,
] as const

export const storageManager = {
  get<T = string>(key: string): T | null {
    try {
      const item = localStorage.getItem(key)
      if (!item) return null

      try {
        return JSON.parse(item) as T
      } catch {
        return item as T
      }
    } catch (error) {
      console.error(`[StorageManager] Failed to get ${key}:`, error)
      return null
    }
  },

  set(key: string, value: unknown): void {
    try {
      const serialized = typeof value === 'string' ? value : JSON.stringify(value)
      localStorage.setItem(key, serialized)
    } catch (error) {
      console.error(`[StorageManager] Failed to set ${key}:`, error)
    }
  },

  remove(key: string): void {
    try {
      localStorage.removeItem(key)
    } catch (error) {
      console.error(`[StorageManager] Failed to remove ${key}:`, error)
    }
  },

  getSession<T = string>(key: string): T | null {
    try {
      const item = sessionStorage.getItem(key)
      if (!item) return null

      try {
        return JSON.parse(item) as T
      } catch {
        return item as T
      }
    } catch (error) {
      console.error(`[StorageManager] Failed to get session ${key}:`, error)
      return null
    }
  },

  setSession(key: string, value: unknown): void {
    try {
      const serialized = typeof value === 'string' ? value : JSON.stringify(value)
      sessionStorage.setItem(key, serialized)
    } catch (error) {
      console.error(`[StorageManager] Failed to set session ${key}:`, error)
    }
  },

  removeSession(key: string): void {
    try {
      sessionStorage.removeItem(key)
    } catch (error) {
      console.error(`[StorageManager] Failed to remove session ${key}:`, error)
    }
  },

  clearSessionData(): void {
    try {
      SESSION_KEYS.forEach((key) => {
        localStorage.removeItem(key)
      })
      sessionStorage.clear()
    } catch (error) {
      console.error('[StorageManager] Failed to clear session data:', error)
    }
  },

  getBusinessId(): string | null {
    return this.get<string>(STORAGE_KEYS.BUSINESS_ID)
  },

  setBusinessId(id: string): void {
    this.set(STORAGE_KEYS.BUSINESS_ID, id)
  },

  getListingId(): string | null {
    return this.get<string>(STORAGE_KEYS.LISTING_ID)
  },

  setListingId(id: string): void {
    this.set(STORAGE_KEYS.LISTING_ID, id)
  },

  isAdmin(): boolean {
    return this.get<boolean>(STORAGE_KEYS.IS_ADMIN) === true
  },

  setIsAdmin(isAdmin: boolean): void {
    this.set(STORAGE_KEYS.IS_ADMIN, isAdmin)
  },

  getAdminViewingBusinessId(): string | null {
    return this.get<string>(STORAGE_KEYS.ADMIN_VIEWING_BUSINESS_ID)
  },

  setAdminViewingBusinessId(businessId: string): void {
    this.set(STORAGE_KEYS.ADMIN_VIEWING_BUSINESS_ID, businessId)
  },

  getAdminViewingBusinessData(): { id: string; legalName: string } | null {
    return this.get<{ id: string; legalName: string }>(STORAGE_KEYS.ADMIN_VIEWING_BUSINESS_DATA)
  },

  setAdminViewingBusinessData(data: { id: string; legalName: string }): void {
    this.set(STORAGE_KEYS.ADMIN_VIEWING_BUSINESS_DATA, data)
  },

  clearAdminViewingContext(): void {
    this.remove(STORAGE_KEYS.ADMIN_VIEWING_BUSINESS_ID)
    this.remove(STORAGE_KEYS.ADMIN_VIEWING_BUSINESS_DATA)
  },
}
